import { ArrowRight } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { UploadSummary } from '../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import { useFeatureFlags } from '../../../../services/featureFlags/hooks/useFeatureFlags';
import TooltipPopup from '../../../commonUI/tooltips/TooltipPopup';
import { TooltipUploadSummary } from '../../../debugInfo/collections/tooltips/TooltipUploadSummary';
import { UploadDetailList } from './UploadDetailList';
import { AnimatedUploadStatus } from './UploadStatus/UploadStatus';

type Props = {
  summary: UploadSummary;
  isExpanded: boolean;
  onExpandedChange: (customerId: string, isExpanded: boolean) => void;
};

export const UploadSummaryListItem = ({
  summary,
  isExpanded,
  onExpandedChange,
}: Props) => {
  const theme = useTheme();
  const { customerName, uploads, customerID } = summary;

  // Handle accordion expand/collapse (state is managed at the parent level to preserve state across data re-fetches)
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, expanded: boolean) => {
      onExpandedChange(customerID, expanded);
    },
    [customerID, onExpandedChange]
  );

  const { Diagnostics } = useFeatureFlags();

  return (
    <Accordion
      key={customerName}
      elevation={0}
      sx={{
        // Remove the dividing lines between the list items
        '::before': {
          content: 'none',
        },
      }}
      expanded={isExpanded}
      onChange={handleChange}
    >
      <TooltipPopup
        colorScheme='extraDark'
        placement='right'
        popupComponent={<TooltipUploadSummary uploadSummary={summary} />}
        customBgColor={theme.vars.palette.background.paper}
        border
        arrow={false}
        enabled={Diagnostics}
      >
        <StyledAccordionSummary
          sx={{
            paddingLeft: 0,
            '&.MuiAccordionSummary-root .MuiAccordionSummary-content': {
              justifyContent: 'space-between',
            },
          }}
          expandIcon={<ArrowRight sx={{ width: '1rem', height: '1rem' }} />}
        >
          <Typography variant='body1'>{customerName}</Typography>
          <AnimatedUploadStatus
            in={!isExpanded}
            status={summary.overallStatus}
            progress={summary.overallPercentUploaded}
          />
        </StyledAccordionSummary>
      </TooltipPopup>
      <AccordionDetails sx={{ paddingLeft: 3, paddingTop: 2 }}>
        <UploadDetailList uploadDetails={uploads} />
        <Divider
          sx={{
            mt: '1.5rem',
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  flexDirection: 'row-reverse',
  textTransform: 'capitalize',
  // Center the icon vertically
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.25rem', // Add gap between arrow and text
    '&.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
  },
  // Center content vertically as well
  '& .MuiAccordionSummary-content': {
    display: 'flex',
    alignItems: 'center',
  },
}));
